import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { financeiroAPI, clientsAPI } from '../../lib/api';
import { useModulosAtivos } from '../../hooks/useModulosAtivos';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CheckCheck, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom';
import ServiceFormModal from './ServiceFormModal';
import ServiceListRow from './ServiceListRow';
import { imprimirRecibo } from './recibo';

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

export default function FinanceiroFinalizados() {
  const { financeiroAtivo, loading: loadingMod } = useModulosAtivos();
  const [services, setServices] = useState([]);
  const [types, setTypes] = useState([]);
  const [methods, setMethods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const svcParams = { finalizados: true };
      if (filters.status) {
        delete svcParams.finalizados;
        svcParams.status = filters.status;
      }
      const [svc, tp, pm, cat, cli] = await Promise.all([
        financeiroAPI.listServices(svcParams),
        financeiroAPI.listServiceTypes(),
        financeiroAPI.listPaymentMethods(),
        financeiroAPI.listCategories(),
        clientsAPI.list(),
      ]);
      setServices(svc.data || []);
      setTypes(tp.data || []);
      setMethods(pm.data || []);
      setCategories(cat.data || []);
      setClients(cli.data || []);
    } catch (e) {
      toast.error('Erro ao carregar serviços finalizados');
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => { if (financeiroAtivo) load(); }, [financeiroAtivo, load]);

  const filtered = useMemo(() => {
    const q = (filters.search || '').toLowerCase();
    if (!q) return services;
    const qDigits = onlyDigits(q);
    return services.filter(s =>
      (s.titulo || '').toLowerCase().includes(q) ||
      (s.cliente_nome || '').toLowerCase().includes(q) ||
      (qDigits && onlyDigits(s.cliente_cpf || '').includes(qDigits))
    );
  }, [services, filters.search]);

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este serviço? Esta ação não pode ser desfeita.')) return;
    try {
      await financeiroAPI.deleteService(id);
      toast.success('Serviço excluído');
      load();
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  const totalReceita = services.filter(s => s.status === 'concluido').reduce((sum, s) => sum + (s.valor_pago || 0), 0);

  if (loadingMod) return <div className="p-10 text-center text-muted-foreground">Carregando módulo...</div>;
  if (!financeiroAtivo) return <Navigate to="/" replace />;

  return (
    <div className="space-y-5" data-testid="financeiro-finalizados">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Serviços Finalizados</h1>
        <p className="text-sm text-muted-foreground">Serviços concluídos ou cancelados</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por título, cliente ou CPF..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                data-testid="filter-search-finalizados"
              />
            </div>
            <Select value={filters.status || 'todos'} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'todos' ? '' : v }))}>
              <SelectTrigger data-testid="filter-status-finalizados"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos finalizados</SelectItem>
                <SelectItem value="concluido">Concluídos</SelectItem>
                <SelectItem value="cancelado">Cancelados</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-right">
              <p className="text-[11px] text-muted-foreground uppercase">Total Recebido</p>
              <p className="text-lg font-bold text-emerald-600">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalReceita)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[11px] uppercase text-muted-foreground font-semibold tracking-wider border-b">
        <div className="col-span-1"></div>
        <div className="col-span-3">Serviço</div>
        <div className="col-span-3">Cliente</div>
        <div className="col-span-2 text-right">Valor</div>
        <div className="col-span-1">Início</div>
        <div className="col-span-1">Status</div>
        <div className="col-span-1 text-center">Manejo</div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-muted-foreground">Carregando...</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <CheckCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
          Nenhum serviço finalizado ainda.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(svc => (
            <ServiceListRow
              key={svc.id}
              svc={svc}
              onChanged={load}
              onEdit={(s) => { setEditing(s); setShowForm(true); }}
              onDelete={handleDelete}
              onPrintRecibo={imprimirRecibo}
              finalizados={true}
            />
          ))}
        </div>
      )}

      {showForm && (
        <ServiceFormModal
          editing={editing}
          types={types}
          methods={methods}
          categories={categories}
          clients={clients}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}
